import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/browser';

import ThroneSeatElements, { SeatMapLegend } from './throne-seat-elements';

import SeatMapContainer from '@weezevent/throne';
import HoverSeat from '../../components/placement/hoverSeat';

import useAppContext from '../../context/useAppContext';
import useSeatedContext from '../../context/seatedContext';
import { ToastContext, StandardHeader, Button, ActionDropdown, ActionItem } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

import { fetcher } from '@weezevent/weezjs-core';
import { default as SideBar } from '../webservicesV2/ticketList/ticketInfoDrawer/portal';
import SeatInfoBlocks from '../../components/placement/seatInfoBlocks';
import SeatTitle from '../../pages/attendee/add-attendee/components/InventoriesStep/ManualModal/ModalSeats/SeatTitle';
import '../../styles/availability/seats.css';
import Style from './seats.module.css';
import ContingentSelector from '../../components/placement/selectedSeats/contingentSelector';
import ItemDisplay from '../../components/placement/itemDisplay';

const ACTION_TYPES = {
    CHANGE_AVAILABILITY: 'change_availability',
    CHANGE_CONTINGENT: 'change_contingent'
};

const AVAILABILITY_ACTIONS = {
    AVAILABLE: 'available',
    UNAVAILABLE: 'unavailable'
};

const STATE_KEY_MAP = {
    AVAILABLE: 'available',
    BOOKED: 'booked',
    BOOKED_BY_RESERVATION: 'reserved',
    PARTIALLY_AVAILABLE: 'reserved',
    PARTIALLY_BOOKED: 'reserved',
    BOOKING_IN_PROGRESS: 'inProgress',
    PARTIALLY_BOOKING_IN_PROGRESS: 'inProgress',
    UNAVAILABLE: 'unavailable',
    LOCKED_FOR_SOCIAL_DISTANCING: 'socialDistancing'
};

const isSeatScanned = seat => seat.active_booking?.attendee?.scanned;

const getStateKey = seat => {
    // Handle scanned seats separately because we have BOOKED state but with scanned flag
    if (seat.booking_state === 'BOOKED' && isSeatScanned(seat)) {
        return 'scanned';
    }

    return STATE_KEY_MAP[seat.booking_state] ?? null;
};

function FooterSideBar({ seats, assignContingent, contingents, currentInventory, rates }) {
    const { t } = useTranslation();

    if (!seats || !contingents || !seats.length || !contingents.length) return null;

    const allSeatsUnavailable = seats.every(seat => seat.contingent === null);
    const canChangeContingent = seats.every(seat => seat.booking_state !== 'BOOKED' && seat.booking_state !== 'BOOKING_IN_PROGRESS');

    const availableContingents = React.useMemo(() => {
        if (!canChangeContingent) return [];

        const contingentSelector = new ContingentSelector({
            seats,
            contingents: contingents.reduce((acc, c) => ({ ...acc, [c.uuid]: c }), {}),
            currentInventory,
            rate: { rates }
        });

        return contingentSelector.getFilteredContingents();
    }, [seats, contingents, currentInventory, rates, canChangeContingent]);

    const handleActionChange = React.useCallback(
        (item, option) => {
            if (!item || !option?.value) return;

            if (item.index === ACTION_TYPES.CHANGE_AVAILABILITY) {
                assignContingent(null, seats);
                return;
            }

            // action by default: change contingent
            const selectedContingent = contingents.find(c => c.uuid === option.value);
            if (selectedContingent) {
                assignContingent(selectedContingent, seats);
            }
            return;
        },
        [assignContingent, seats, contingents]
    );

    // Prepare action options compared to the seats selected
    const actionOptions = React.useMemo(() => {
        const options = [];

        if (canChangeContingent && !allSeatsUnavailable) {
            options.push({
                key: ACTION_TYPES.CHANGE_AVAILABILITY,
                labelName: t('seats_selection_change_availability_action'),
                options: [
                    {
                        key: AVAILABILITY_ACTIONS.UNAVAILABLE,
                        text: t('seats_selection_set_unavailable_action'),
                        value: AVAILABILITY_ACTIONS.UNAVAILABLE
                    }
                ]
            });
        }

        if (canChangeContingent && availableContingents.length > 0) {
            options.push({
                key: ACTION_TYPES.CHANGE_CONTINGENT,
                labelName: t('seats_selection_change_contingent_action'),
                options: availableContingents.map(contingent => ({
                    key: contingent.uuid,
                    text: contingent.name,
                    value: contingent.uuid
                }))
            });
        }

        return options;
    }, [canChangeContingent, availableContingents]);

    if (actionOptions.length === 0) return null;

    return (
        <div className={Style['footer-button-container']} onClick={e => e.stopPropagation()}>
            <ActionDropdown
                label={t('seats_selection_dropdown_label', { count: seats.length })}
                onChange={handleActionChange}
                onClick={e => e.stopPropagation()}
                showCta={true}
                ctaLabel={t('ticket.common.action.save')}
            >
                {actionOptions.map(option => (
                    <ActionItem onClick={e => e.stopPropagation()} key={option.key} labelName={option.labelName} index={option.key} options={option.options} />
                ))}
            </ActionDropdown>
        </div>
    );
}

FooterSideBar.propTypes = {
    seats: PropTypes.array,
    assignContingent: PropTypes.func,
    contingents: PropTypes.array,
    currentInventory: PropTypes.object,
    rates: PropTypes.array,
    zone: PropTypes.object,
    organization: PropTypes.object,
    event: PropTypes.object
};

function Seat({ seat, contingents, categories, organization, event }) {
    const { t } = useTranslation();

    const seatContingent = React.useMemo(() => {
        return contingents.find(c => c.uuid === seat.contingent);
    }, [seat, contingents]);

    const seatCategory = React.useMemo(() => {
        return categories.find(c => c.uuid === seat.category);
    }, [seat, categories]);

    return (
        <div className={Style['seat-container']}>
            <div className={Style['seat-info-drawer']}>
                <div className={Style['seat-info-drawer__header']} style={{ backgroundColor: seatContingent?.color || '#878C9B' }}>
                    <SeatTitle seat={seat} color={'#fff'} />
                </div>
                <div className={Style['seat-info-drawer__content']}>
                    <div className={Style['seat-info-drawer__info']}>
                        <SeatInfoBlocks activeBooking={seat.active_booking} bookingState={seat.booking_state} mode="detail" />
                        {seatContingent && <ItemDisplay item={seatContingent} />}
                        {seatCategory && <ItemDisplay item={seatCategory} type="category" />}
                    </div>

                    {seat?.active_booking?.attendee && (
                        <div className={Style['attendee-details']}>
                            <Button
                                inverted
                                label={t('ticket.availability.drawer.attendee_details')}
                                onClick={() => window.open(`/ticket/O${organization.id}/E${event.id}/manager/attendees/${seat.active_booking.attendee.id}`)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SeatInfoDrawer({
    seats = [],
    contingents,
    categories,
    openSideBar = false,
    onCloseSideBar = () => {},
    assignContingent = () => {},
    currentInventory,
    rates,
    zone,
    organization,
    event,
    ignoreClickOnRefs = []
}) {
    const { t } = useTranslation();

    if (!seats?.length) {
        return null;
    }

    return (
        <SideBar className="drawer" open={openSideBar} onClickAway={onCloseSideBar} onClose={onCloseSideBar} size="mini" ignoreClickOnRefs={ignoreClickOnRefs}>
            <StandardHeader title={t('ticket.availability.drawer.title')} />
            <div className={Style['seat-info-drawer__seats']}>
                {seats.map(seat => (
                    <Seat
                        key={seat.uuid}
                        seat={seat}
                        seats={seats}
                        contingents={contingents}
                        categories={categories}
                        assignContingent={assignContingent}
                        currentInventory={currentInventory}
                        rates={rates}
                        zone={zone}
                        organization={organization}
                        event={event}
                    />
                ))}
            </div>
            <FooterSideBar
                seats={seats}
                assignContingent={assignContingent}
                contingents={contingents}
                currentInventory={currentInventory}
                rates={rates}
                zone={zone}
                organization={organization}
                event={event}
            />
        </SideBar>
    );
}

SeatInfoDrawer.propTypes = {
    seats: PropTypes.array,
    contingents: PropTypes.array,
    categories: PropTypes.array,
    openSideBar: PropTypes.bool,
    onCloseSideBar: PropTypes.func,
    assignContingent: PropTypes.func,
    currentInventory: PropTypes.object,
    rates: PropTypes.array,
    zone: PropTypes.object,
    organization: PropTypes.object,
    event: PropTypes.object,
    ignoreClickOnRefs: PropTypes.array
};

export default function Seats({ availability, contingents, categories, rates = [], onBack = () => {}, onContingentChanged = () => {}, display }) {
    const [hoveredSeat, setHoveredSeat] = React.useState(null);
    const [selectedStates, setSelectedStates] = React.useState([]);
    const [manuallySelectedSeats, setManuallySelectedSeats] = React.useState([]);
    const [{ inventory }] = useSeatedContext();
    const [{ organization, event }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const legendRef = React.useRef(null);

    let filteredContingents = contingents
        ? contingents.filter(contingent => {
              let contingentRates = rates.filter(rate => {
                  return rate.resolved_contingents.indexOf(contingent.uuid > -1);
              });
              // At least one rate has the current inventory id
              return contingentRates.length && contingentRates.some(rate => rate.inventories.indexOf(inventory.uuid) > -1);
          })
        : [];

    const handleStateToggle = React.useCallback(stateKey => {
        setSelectedStates(prev => {
            if (prev.length === 0) {
                setManuallySelectedSeats([]);
            }
            const newStates = prev.includes(stateKey) ? prev.filter(s => s !== stateKey) : [...prev, stateKey];
            return newStates;
        });
    }, []);

    // Compute selected seats: use filtered seats if filters are active, otherwise use manually selected seats
    const selectedSeats = React.useMemo(() => {
        if (selectedStates.length > 0) {
            return (
                availability?.seats?.filter(seat => {
                    const seatStateKey = getStateKey(seat);
                    return seatStateKey && selectedStates.includes(seatStateKey);
                }) || []
            );
        }
        return manuallySelectedSeats;
    }, [selectedStates, manuallySelectedSeats, availability]);

    const handleCloseSideBar = React.useCallback(() => {
        setSelectedStates([]);
        setManuallySelectedSeats([]);
    }, []);

    const updateContingent = React.useCallback(
        (contingent, seats) => {
            let url = `/ticket/organizations/${organization.id}/events/${event.id}/inventories/${inventory.uuid}/seats/set_contingents`;
            fetcher
                .post(url, {
                    data: {
                        seats: seats.map(seat => seat.uuid),
                        contingent_uuid: contingent ? contingent.pk : null
                    }
                })
                .then(({ status, response }) => {
                    if (status !== 200) {
                        console.error('[Seats][Change contingent]', response);
                        throw new Error('Could not change contingent for seats');
                    }

                    // todo reload this page
                    onContingentChanged();
                    toast.success(t('ticket.availability.seats_update_success'));
                })
                .catch(e => {
                    // Catch sentry
                    Sentry.captureException(e);
                    toast.error(t('ticket.availability.seats_update_error'));
                });
        },
        [organization, event, inventory, onContingentChanged]
    );

    const normalizeSeatState = seat => ({
        ...seat,
        state: isSeatScanned(seat) ? 'scanned' : seat.booking_state?.toLowerCase()
    });

    // Normalize seat states for Throne rendering
    const normalizedAvailability = React.useMemo(() => {
        if (!availability?.seats) return availability;

        return {
            ...availability,
            seats: availability.seats.map(normalizeSeatState)
        };
    }, [availability]);

    return (
        <>
            <SeatInfoDrawer
                seats={selectedSeats}
                contingents={filteredContingents}
                categories={categories}
                currentInventory={inventory}
                rates={rates}
                zone={availability}
                assignContingent={updateContingent}
                openSideBar={selectedSeats?.length > 0}
                organization={organization}
                event={event}
                onCloseSideBar={handleCloseSideBar}
                ignoreClickOnRefs={[legendRef]}
            />
            <SeatMapContainer
                showScene={false}
                showPopup={!selectedSeats?.length}
                minimap={false}
                zone={normalizedAvailability}
                selectedSeats={selectedSeats}
                onSeatOver={seat => {
                    if (selectedSeats?.length) {
                        return;
                    }

                    setHoveredSeat(seat);
                }}
                onSeatsSelected={setManuallySelectedSeats}
                onSeatClicked={s => {
                    setManuallySelectedSeats([s]);
                }}
                mapOptions={{
                    showRowNumbers: true,
                    zoomControls: false,
                    offset: false,
                    seatElements: ThroneSeatElements
                }}
                popover={<HoverSeat zone={hoveredSeat} contingents={filteredContingents} categories={categories} display={display} />}
            />
            <SeatMapLegend ref={legendRef} seats={normalizedAvailability.seats} selectedStates={selectedStates} onStateToggle={handleStateToggle} />
        </>
    );
}

Seats.propTypes = {
    availability: PropTypes.object.isRequired,
    contingents: PropTypes.array.isRequired,
    categories: PropTypes.array.isRequired,
    rates: PropTypes.array.isRequired,
    onBack: PropTypes.func,
    onContingentChanged: PropTypes.func
};
